import React, { ChangeEvent, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { PutBlobResult, put } from "@vercel/blob";
import { v4 as uuid } from "uuid";
import { usePersistStore, useStore } from "../store";
import { BlobService, PaymentService } from "../services";
import Utils from "../utils/utils";

interface Props {
  show: boolean;
  onHide: () => void;
  data: any;
}

const MAX_LENGTH = 5000;
const MIN_LENGTH = 10;
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILE_TYPES =
  "image/png, image/jpg, image/jpeg, image/webp, video/mp4, video/quicktime, application/pdf";

const formatBytes = (bytes: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconForFile = (file: File): string => {
  if (file.type.startsWith("image/")) return "bi-image";
  if (file.type.startsWith("video/")) return "bi-camera-video";
  return "bi-file-earmark-text";
};

const TrustapComplaintPopup: React.FC<Props> = ({ show, onHide, data }) => {
  const { token } = usePersistStore((s: any) => s);
  const { setSuccessMessage, setIsLoading } = useStore((s: any) => s);
  const [text, setText] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptFiles = (incoming: FileList | File[] | null | undefined) => {
    if (!incoming) return;
    const incomingList = Array.from(incoming);
    const next: File[] = [...files];
    let rejected = "";
    for (const f of incomingList) {
      if (next.length >= MAX_FILES) {
        rejected = `You can attach up to ${MAX_FILES} files.`;
        break;
      }
      if (f.size > MAX_FILE_BYTES) {
        rejected = `${f.name} is larger than 10 MB.`;
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    setValidationMessage(rejected);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    acceptFiles(e.target.files);
    // Reset the input so picking the same file again still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (i: number) => {
    setFiles(files.filter((_, idx) => idx !== i));
    setValidationMessage("");
  };

  const handleSubmit = async (e: any) => {
    e?.preventDefault();
    // Guard against double-submit. `disableButton` is the authoritative flag;
    // the explicit check here covers the tiny window before React re-renders
    // the disabled attribute after the first click.
    if (disableButton) return;
    const trimmed = text.trim();
    if (!trimmed)
      return setValidationMessage("Please provide your complaint.");
    if (trimmed.length < MIN_LENGTH)
      return setValidationMessage(
        `Please describe your complaint in at least ${MIN_LENGTH} characters.`
      );
    if (trimmed.length > MAX_LENGTH)
      return setValidationMessage(
        `Complaint cannot exceed ${MAX_LENGTH} characters.`
      );

    setDisableButton(true);
    setIsLoading(true);

    // Trustap's complaint endpoint only accepts a `description` string —
    // no native file attachments. So we upload each piece of evidence to
    // Vercel Blob (same flow as OpenDisputeModal) and append the public
    // URLs to the description so the support reviewer can click through.
    const uploadedUrls: string[] = [];
    for (const file of files) {
      const ext = Utils.getFileExtension(file.name);
      const filename = `${uuid()}.${ext}`;
      const tokenResult = await BlobService.getClientToken(token, { filename });
      if (tokenResult.error) {
        setDisableButton(false);
        setIsLoading(false);
        setValidationMessage(tokenResult.error);
        return;
      }
      const clientToken = tokenResult?.data?.token;
      const blob: PutBlobResult = await put(filename, file, {
        access: "public",
        token: clientToken,
      });
      if (!blob) {
        setDisableButton(false);
        setIsLoading(false);
        setValidationMessage("Failed to upload evidence. Please try again.");
        return;
      }
      uploadedUrls.push(blob.url);
    }

    const description =
      uploadedUrls.length > 0
        ? `${trimmed}\n\nEvidence:\n${uploadedUrls.join("\n")}`
        : trimmed;

    let result = await PaymentService.createTrustapComplaint(token, {
      description,
      paymentId: data?._id,
    });
    if (result?.error) {
      setDisableButton(false);
      setIsLoading(false);
      setValidationMessage(result?.error);
      return;
    }

    setIsLoading(false);
    setSuccessMessage("Complaint submitted successfully.");
    clear();
    onHide();
  };

  const clear = () => {
    setText("");
    setFiles([]);
    setValidationMessage("");
    setDisableButton(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    clear();
    onHide();
  };

  const charCount = text.length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="confirmModal"
    >
      <Modal.Body>
        <div
          className="confirmModal__icon"
          style={{ background: "#fff6df", color: "#b36d00" }}
        >
          <i className="bi bi-flag" aria-hidden="true" />
        </div>
        <h2 className="confirmModal__title">Raise a complaint</h2>
        <p className="confirmModal__subtitle">
          Tell us what went wrong with this order. Our team and Trustap will
          review your complaint and get back to you.
        </p>

        <div className="confirmModal__details">
          <div className="confirmModal__detailRow">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <span>
              Be specific about what happened, when, and any evidence you have
              (photos, messages, receipts).
            </span>
          </div>
          <div className="confirmModal__detailRow">
            <i className="bi bi-shield-check" aria-hidden="true" />
            <span>
              Payment will stay on hold with Trustap while the complaint is
              open.
            </span>
          </div>
        </div>

        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="complaintModal__label mb-0">
              Your complaint
            </Form.Label>
            <span
              className={`complaintModal__count${
                charCount > MAX_LENGTH
                  ? " complaintModal__count--danger"
                  : charCount < MIN_LENGTH && charCount > 0
                  ? " complaintModal__count--warn"
                  : ""
              }`}
            >
              {charCount}/{MAX_LENGTH}
            </span>
          </div>
          <Form.Control
            as="textarea"
            rows={5}
            className="complaintModal__textarea"
            placeholder="Describe what happened. Include relevant dates, conversations, and any evidence you can reference."
            value={text}
            onChange={(e: any) => {
              setText(e?.target?.value);
              if (validationMessage) setValidationMessage("");
            }}
            maxLength={MAX_LENGTH + 1}
          />
        </Form.Group>

        <div className="complaintModal__evidence">
          <div className="complaintModal__evidenceHeader">
            <span className="complaintModal__label">Evidence (optional)</span>
            <span className="complaintModal__hint">
              {files.length}/{MAX_FILES} · images, videos, or PDF up to 10 MB
            </span>
          </div>

          {files.length > 0 && (
            <ul className="complaintModal__fileList">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="complaintModal__file">
                  <i
                    className={`bi ${iconForFile(f)} complaintModal__fileIcon`}
                    aria-hidden="true"
                  />
                  <div className="complaintModal__fileInfo">
                    <div className="complaintModal__fileName" title={f.name}>
                      {f.name}
                    </div>
                    <div className="complaintModal__fileMeta">
                      {formatBytes(f.size)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="complaintModal__fileRemove"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => removeFile(i)}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {files.length < MAX_FILES && (
            <button
              type="button"
              className="complaintModal__upload"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="bi bi-paperclip" aria-hidden="true" />
              <span>
                {files.length > 0
                  ? "Add more evidence"
                  : "Attach photos, videos, or receipts"}
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            className="d-none"
            onChange={handleFileChange}
          />
        </div>

        {validationMessage && (
          <div className="confirmModal__alert confirmModal__alert--danger">
            <i className="bi bi-exclamation-circle" aria-hidden="true" />
            <span>{Utils.capitalizeFirstLetter(validationMessage)}</span>
          </div>
        )}

        <div className="confirmModal__footer">
          <button type="button" className="closeBtn" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="confirmModal__primary"
            onClick={handleSubmit}
            disabled={disableButton}
          >
            Submit complaint
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TrustapComplaintPopup;
